import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import AuthGuard from '../components/auth/AuthGuard';
import AppShell from '../components/layout/AppShell';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PermissionGuard from '../components/auth/PermissionGuard';

// Lazy loading para rotas pesadas
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const InviteAcceptPage = React.lazy(() => import('../pages/InviteAcceptPage'));
const ForgotPasswordPage = React.lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage'));
const EmailVerifiedPage = React.lazy(() => import('../pages/EmailVerifiedPage'));
const ProfilePage = React.lazy(() => import('../pages/ProfilePage'));
const LeadsPage = React.lazy(() => import('../pages/LeadsPage'));
const CategoriesPage = React.lazy(() => import('../pages/CategoriesPage'));
const ColorsPage = React.lazy(() => import('../pages/ColorsPage'));
const PersonalizationTypesPage = React.lazy(() =>
  import('../pages/PersonalizationTypesPage')
);
const ProductsPage = React.lazy(() => import('../pages/ProductsPage'));
const ProductDetailPage = React.lazy(() => import('../pages/ProductDetailPage'));
const SuppliersPage = React.lazy(() => import('../pages/SuppliersPage'));
const PricingMatrixPage = React.lazy(() => import('../pages/PricingMatrixPage'));
const UsersManagementPage = React.lazy(() => import('../pages/UsersManagementPage'));

// Componente de loading para Suspense
const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
    }}
  >
    <LoadingSpinner size="large" />
  </div>
);

const DefaultRedirect = () => {
  return <Navigate to="/profile" replace />;
};

const AppRoutes = () => {
  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Rotas públicas */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite/accept" element={<InviteAcceptPage />} />
          <Route path="/email-verified" element={<EmailVerifiedPage />} />

          {/* Rotas protegidas */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <AppShell>
                  <DefaultRedirect />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/profile"
            element={
              <AuthGuard>
                <AppShell>
                  <ProfilePage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/users-management"
            element={
              <AuthGuard>
                <AppShell>
                  <UsersManagementPage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/invitations"
            element={
              <AuthGuard>
                <AppShell>
                  <Navigate to="/users-management?tab=invitations" replace />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/leads"
            element={
              <AuthGuard>
                <AppShell>
                  <PermissionGuard permission="leads.view">
                    <LeadsPage />
                  </PermissionGuard>
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/categories"
            element={
              <AuthGuard>
                <AppShell>
                  <CategoriesPage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/colors"
            element={
              <AuthGuard>
                <AppShell>
                  <ColorsPage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/personalization-types"
            element={
              <AuthGuard>
                <AppShell>
                  <PersonalizationTypesPage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/products/:productId"
            element={
              <AuthGuard>
                <AppShell>
                  <ProductDetailPage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/products"
            element={
              <AuthGuard>
                <AppShell>
                  <ProductsPage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/suppliers"
            element={
              <AuthGuard>
                <AppShell>
                  <SuppliersPage />
                </AppShell>
              </AuthGuard>
            }
          />

          <Route
            path="/pricing-matrix-rules"
            element={
              <AuthGuard>
                <AppShell>
                  <PricingMatrixPage />
                </AppShell>
              </AuthGuard>
            }
          />

          {/* Rota 404 */}
          <Route
            path="*"
            element={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '100vh',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <h1>404 - Página não encontrada</h1>
                <button onClick={() => (window.location.href = '/profile')}>
                  Voltar ao início
                </button>
              </div>
            }
          />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
